import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  LoadingSkeleton,
  Modal,
  Pagination,
  Select,
  Tabs,
  Textarea,
} from '@nexora/design-system';
import {
  useClosingPeriods,
  useClosingReconciliation,
  useUpdateConciliacao,
} from './useClosing';
import {
  STATUS_CONCILIACAO_LABEL,
  type Conciliacao,
  type ConciliacaoStatus,
} from './types';
import { formatCount, formatCurrency } from '../../shared/utils/format';

/* ═══════════════════════════════════════════════════════════════════════════
   Conciliação Fiscal × Contábil (Discrepancy Variance)
   ───────────────────────────────────────────────────────────────────────────
   Contrato consumido (rota real /api/v1/closing):

   GET  /closing/periods                    → Paginated<FechamentoPeriodo>
     (seletor de período)

   GET  /closing/periods/:id/reconciliation { status?, accountCode?, page,
     limit } → Paginated<Conciliacao>
     Conciliacao: { id, accountCode, description, accountingLedger (R$),
       taxRecord (R$), variance (R$), absVariance, temDivergencia,
       status (CONCILIADO|DIVERGENTE|PENDENTE|IGNORADO), observacao,
       tipoAjuste, ajusteProposto }

   PATCH /closing/periods/:id/reconciliation/:itemId { status?, observacao? }
     (status alvo: CONCILIADO | DIVERGENTE | IGNORADO — PENDENTE não é
     atingível via API)

   Colunas da tela de referência: Account Code, Description, Accounting
   Ledger (R$), Tax Record (R$), Variance, Status, Action.

   Observação de fidelidade: o contrato não expõe uma série temporal de
   "Tax Burden Trend" — ele entrega a lista plana de itens do período. O bloco
   "Carga Tributária vs Resultado Contábil" é uma AGGREGAÇÃO honesta dos itens
   exibidos (soma fiscal × soma contábil, barras proporcionais em div/CSS),
   rotulada como tal — não como um gráfico de tendência servido pelo backend.
   ═══════════════════════════════════════════════════════════════════════════ */

const STATUS_CONCILIACAO_BADGE: Record<
  ConciliacaoStatus,
  'success' | 'warning' | 'error' | 'info' | 'neutral'
> = {
  CONCILIADO: 'success',
  DIVERGENTE: 'error',
  PENDENTE: 'warning',
  IGNORADO: 'neutral',
};

type RecTab = 'items' | 'summary';

export function ClosingReconciliationPage(): JSX.Element {
  const [periodId, setPeriodId] = useState<string | undefined>(undefined);
  const [tab, setTab] = useState<RecTab>('items');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Conciliacao | null>(null);
  const [editStatus, setEditStatus] = useState<ConciliacaoStatus>('CONCILIADO');
  const [editObservacao, setEditObservacao] = useState('');

  const periodsQuery = useClosingPeriods({ limit: 50 });
  const periods = periodsQuery.data?.data ?? [];
  const activePeriodId = periodId ?? periods[0]?.id;

  const recQuery = useClosingReconciliation(activePeriodId, {
    status: statusFilter || undefined,
    page,
    limit: 20,
  });
  const updateItem = useUpdateConciliacao(activePeriodId);

  const rows = recQuery.data?.data ?? [];
  const totalPages = recQuery.data?.totalPages ?? 1;

  const totals = useMemo(() => {
    let ledger = 0;
    let tax = 0;
    let divergencias = 0;
    rows.forEach((r) => {
      ledger += r.accountingLedger ?? 0;
      tax += r.taxRecord ?? 0;
      if (r.temDivergencia || r.status === 'DIVERGENTE') divergencias += 1;
    });
    return { ledger, tax, divergencias };
  }, [rows]);

  const handleFilter = (nextStatus: string): void => {
    setStatusFilter(nextStatus);
    setPage(1);
  };

  const handleOpenEdit = (item: Conciliacao): void => {
    setEditing(item);
    setEditStatus(item.status === 'PENDENTE' ? 'CONCILIADO' : item.status);
    setEditObservacao(item.observacao ?? '');
  };

  const handleSaveEdit = async (): Promise<void> => {
    if (!editing) return;
    try {
      await updateItem.mutateAsync({
        itemId: editing.id,
        input: {
          status: editStatus === 'PENDENTE' ? 'CONCILIADO' : editStatus,
          observacao: editObservacao.trim() || undefined,
        },
      });
      setEditing(null);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Erro ao salvar conciliação.');
    }
  };

  return (
    <div className="closing-reconciliation-page">
      {/* Cabeçalho */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <p className="font-label-sm text-label-sm text-on-surface-variant mb-1 uppercase tracking-wider">
            Fechamento Mensal / Conciliação
          </p>
          <h1 className="font-display-lg text-display-lg text-primary m-0">Conciliação Fiscal × Contábil</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Divergências entre o registro contábil (Accounting Ledger) e o fiscal (Tax Record).
          </p>
        </div>
        <div style={{ flex: '0 0 280px' }}>
          <Select
            label="Período de fechamento"
            value={activePeriodId ?? ''}
            onChange={(e) => setPeriodId(e.target.value)}
            selectSize="sm"
            placeholder="Selecione um período"
          >
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Erro */}
      {periodsQuery.error || recQuery.error ? (
        <Card variant="outlined" padding="1.5rem" className="mb-lg">
          <p className="m-0" style={{ fontWeight: 600, color: '#ba1a1a' }}>Erro ao carregar a conciliação</p>
          <p className="m-0" style={{ fontSize: '0.875rem', color: '#74777f', marginTop: '0.25rem' }}>
            {recQuery.error instanceof Error
              ? recQuery.error.message
              : periodsQuery.error instanceof Error
                ? periodsQuery.error.message
                : 'Erro desconhecido'}
          </p>
          <Button variant="primary" size="sm" onClick={() => recQuery.refetch()} style={{ marginTop: '0.75rem' }}>
            Tentar novamente
          </Button>
        </Card>
      ) : null}

      <Tabs
        tabs={[
          { key: 'items', label: 'Conciliação' },
          { key: 'summary', label: 'Resumo e Análise' },
        ]}
        value={tab}
        onChange={(k) => setTab(k as RecTab)}
        aria-label="Conciliação fiscal x contábil"
        className="mb-lg"
      />

      {tab === 'items' && !recQuery.isLoading && (
        <Card variant="outlined" padding="1rem" className="mb-lg">
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '0 0 220px' }}>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => handleFilter(e.target.value)}
                selectSize="sm"
              >
                <option value="">Todos os status</option>
                {(Object.keys(STATUS_CONCILIACAO_LABEL) as ConciliacaoStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_CONCILIACAO_LABEL[s]}
                  </option>
                ))}
              </Select>
            </div>
            <Button variant="secondary" size="sm" onClick={() => handleFilter('')}>
              Limpar
            </Button>
          </div>
        </Card>
      )}

      {tab === 'items' && (
        <Card variant="outlined" padding="0">
          <CardHeader className="px-md pt-md">
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">
              Itens de conciliação
              {recQuery.data && (
                <span className="font-body-sm text-body-sm text-on-surface-variant font-normal ml-sm">
                  ({formatCount(recQuery.data.total)} itens)
                </span>
              )}
            </h3>
          </CardHeader>

          {recQuery.isLoading && (
            <div style={{ padding: '2rem' }}>
              <LoadingSkeleton count={5} height="40px" borderRadius="8px" />
            </div>
          )}

          {!recQuery.isLoading && !recQuery.error && rows.length === 0 && (
            <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#74777f' }}>
              <p className="m-0" style={{ fontSize: '0.875rem' }}>
                Nenhum item de conciliação {statusFilter ? `com status "${STATUS_CONCILIACAO_LABEL[statusFilter as ConciliacaoStatus]}"` : ''} para este período.
              </p>
            </div>
          )}

          {!recQuery.isLoading && !recQuery.error && rows.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr>
                    {['Account Code', 'Description', 'Accounting Ledger (R$)', 'Tax Record (R$)', 'Variance (R$)', 'Status', 'Action'].map(
                      (h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: ['Accounting Ledger (R$)', 'Tax Record (R$)', 'Variance (R$)'].includes(h) ? 'right' : 'left',
                            padding: '0.75rem 1rem',
                            background: '#f2f4f6',
                            color: '#43474e',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            borderBottom: '2px solid #e0e3e5',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} style={r.temDivergencia ? { background: 'rgba(186,26,26,0.03)' } : undefined}>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', fontWeight: 600 }}>
                        {r.accountCode}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                        {r.description ?? '—'}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {r.accountingLedger != null ? formatCurrency(r.accountingLedger) : '—'}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {r.taxRecord != null ? formatCurrency(r.taxRecord) : '—'}
                      </td>
                      <td
                        style={{
                          padding: '0.75rem 1rem',
                          borderBottom: '1px solid #eceef0',
                          textAlign: 'right',
                          whiteSpace: 'nowrap',
                          fontWeight: 600,
                          color: r.temDivergencia ? '#ba1a1a' : '#2e7d32',
                        }}
                      >
                        {r.variance != null ? formatCurrency(r.variance) : '—'}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                        <Badge variant={STATUS_CONCILIACAO_BADGE[r.status]} size="sm" dot>
                          {STATUS_CONCILIACAO_LABEL[r.status]}
                        </Badge>
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap' }}>
                        <Button variant="outline" size="sm" onClick={() => handleOpenEdit(r)}>
                          Conciliar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!recQuery.isLoading && !recQuery.error && totalPages > 1 && (
            <div style={{ padding: '1rem', borderTop: '1px solid #eceef0' }}>
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </div>
          )}
        </Card>
      )}

      {tab === 'summary' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-lg">
          {/* KPI */}
          <div className="lg:col-span-4">
            <Card variant="outlined" padding="lg" className="mb-lg">
              <h3 className="font-headline-sm text-headline-sm text-on-surface mb-md">Totais da página atual</h3>
              <div className="space-y-md">
                <div>
                  <div className="font-label-md text-label-md text-on-surface-variant">Accounting Ledger (R$)</div>
                  <div className="font-headline-sm text-headline-sm text-on-surface" style={{ fontWeight: 700 }}>
                    {formatCurrency(totals.ledger)}
                  </div>
                </div>
                <div>
                  <div className="font-label-md text-label-md text-on-surface-variant">Tax Record (R$)</div>
                  <div className="font-headline-sm text-headline-sm text-on-surface" style={{ fontWeight: 700 }}>
                    {formatCurrency(totals.tax)}
                  </div>
                </div>
                <div>
                  <div className="font-label-md text-label-md text-on-surface-variant">Variação total (R$)</div>
                  <div
                    className="font-headline-sm text-headline-sm"
                    style={{ fontWeight: 700, color: totals.ledger - totals.tax === 0 ? '#2e7d32' : '#ba1a1a' }}
                  >
                    {formatCurrency(totals.ledger - totals.tax)}
                  </div>
                </div>
                <div>
                  <div className="font-label-md text-label-md text-on-surface-variant">Itens divergentes</div>
                  <div
                    className="font-headline-sm text-headline-sm"
                    style={{ fontWeight: 700, color: totals.divergencias > 0 ? '#ba1a1a' : '#2e7d32' }}
                  >
                    {formatCount(totals.divergencias)}
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Gráfico: Carga Tributária vs Resultado Contábil */}
          <div className="lg:col-span-8">
            <Card variant="outlined" padding="lg">
              <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">
                Carga Tributária vs Resultado Contábil
              </h3>
              <p className="font-label-sm text-label-sm text-on-surface-variant m-0" style={{ marginTop: '0.25rem' }}>
                Agregado dos itens de conciliação desta página — o contrato (ConciliacaoDto) não expõe série temporal de tendência.
              </p>
              {(recQuery.isLoading ? (
                <div style={{ marginTop: '1rem' }}>
                  <LoadingSkeleton count={2} height="56px" borderRadius="8px" />
                </div>
              ) : (
                <div style={{ marginTop: '1.25rem' }}>
                  {(() => {
                    const base = Math.max(totals.ledger, totals.tax, 1);
                    return (
                      <>
                        <div className="mb-md">
                          <div className="flex justify-between font-label-md text-label-md text-on-surface-variant mb-xs">
                            <span>Registro contábil (Accounting)</span>
                            <span className="font-bold text-on-surface">{formatCurrency(totals.ledger)}</span>
                          </div>
                          <div style={{ height: '14px', background: '#eceef0', borderRadius: '7px', overflow: 'hidden' }}>
                            <div
                              style={{ height: '100%', width: `${(totals.ledger / base) * 100}%`, background: '#002045', borderRadius: '7px' }}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between font-label-md text-label-md text-on-surface-variant mb-xs">
                            <span>Carga tributária (Tax)</span>
                            <span className="font-bold text-on-surface">{formatCurrency(totals.tax)}</span>
                          </div>
                          <div style={{ height: '14px', background: '#eceef0', borderRadius: '7px', overflow: 'hidden' }}>
                            <div
                              style={{ height: '100%', width: `${(totals.tax / base) * 100}%`, background: '#ba1a1a', borderRadius: '7px' }}
                            />
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              ))}
            </Card>
          </div>
        </div>
      )}

      {/* Modal de conciliação */}
      {editing && (
        <Modal
          isOpen
          onClose={() => setEditing(null)}
          title={`Conciliar — ${editing.accountCode}`}
          size="md"
          footer={
            <>
              <Button variant="ghost" size="md" onClick={() => setEditing(null)} disabled={updateItem.isPending}>
                Cancelar
              </Button>
              <Button variant="primary" size="md" onClick={handleSaveEdit} loading={updateItem.isPending}>
                Salvar
              </Button>
            </>
          }
        >
          <p className="font-body-sm text-body-sm text-on-surface-variant m-0" style={{ marginBottom: '1rem' }}>
            {editing.description ?? 'Sem descrição'} ·{' '}
            <strong className="text-on-surface">Ledger {editing.accountingLedger != null ? formatCurrency(editing.accountingLedger) : '—'}</strong>{' '}
            vs <strong className="text-on-surface">Fiscal {editing.taxRecord != null ? formatCurrency(editing.taxRecord) : '—'}</strong>
          </p>
          <div className="space-y-md">
            <Select
              label="Status da conciliação"
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value as ConciliacaoStatus)}
            >
              <option value="CONCILIADO">Conciliado</option>
              <option value="DIVERGENTE">Divergente</option>
              <option value="IGNORADO">Ignorado</option>
            </Select>
            <Textarea
              label="Observação"
              placeholder="Justificativa da conciliação (opcional)"
              rows={3}
              value={editObservacao}
              onChange={(e) => setEditObservacao(e.target.value)}
            />
          </div>
        </Modal>
      )}
    </div>
  );
}